import { KnzDiagnostic, KnzParseResult, RegistryLookup } from '../types';
import { buildTree } from './buildTree';
import { parseFrontmatter } from './frontmatter';
import { ParsedBlock, parseBlock } from './parseBlock';
import { tokenize } from './tokenize';

export * from './inline';
export type { RawBlock } from './tokenize';
export type { ParsedBlock } from './parseBlock';

/**
 * knz ソース全体をパースし、frontmatter・ルートノード・診断を返す。
 *
 * パーサは純粋関数であり、registry の中身には依存しない。
 * raw 判定・name の登録有無だけを `registry`（RegistryLookup）経由で照会する。
 */
export const parseKnz = (source: string, registry: RegistryLookup): KnzParseResult => {
  const lines = source.split('\n');
  const diagnostics: KnzDiagnostic[] = [];

  // 1) frontmatter
  const fm = parseFrontmatter(lines);
  diagnostics.push(...fm.diagnostics);

  // 2) 本文をトークン化
  const bodyLines = lines.slice(Math.max(0, fm.bodyStartLine - 1));
  const baseLine = fm.bodyStartLine === 0 ? 1 : fm.bodyStartLine;
  const tok = tokenize(bodyLines, baseLine, registry);
  diagnostics.push(...tok.diagnostics);

  // 3) 各ブロックを解析
  const parsed: ParsedBlock[] = [];
  for (const raw of tok.blocks) {
    // 未登録 name は描画しない（error にはしないが警告）
    if (!registry.hasBlock(raw.name)) {
      diagnostics.push({
        severity: 'warning',
        message: `未登録のコンポーネントです（描画されません）: ${raw.name}`,
        line: raw.startLine,
      });
      continue;
    }
    const pb = parseBlock(raw, registry);
    diagnostics.push(...pb.diagnostics);
    parsed.push(pb.block);
  }

  // 4) ツリー構築（案C）
  const tree = buildTree(parsed);
  diagnostics.push(...tree.diagnostics);

  const isValid = !diagnostics.some((d) => d.severity === 'error');

  return {
    frontmatter: fm.frontmatter,
    roots: tree.roots,
    diagnostics,
    isValid,
  };
};

/**
 * カーソル位置（0-indexed の行番号）が属するブロック名を返す（補完の文脈判定用）。
 * どのブロックにも属さない場合は null。
 */
export const resolveBlockAt = (source: string, lineIndex: number): string | null => {
  const lines = source.split('\n');
  const OPEN_RE = /^<---\s*([A-Za-z][\w]*)\s*--->$/;
  const CLOSE_RE = /^<---\s*!([A-Za-z][\w]*)\s*--->$/;

  let current: string | null = null;
  for (let i = 0; i <= lineIndex && i < lines.length; i++) {
    const t = lines[i].trim();
    const open = t.match(OPEN_RE);
    const close = t.match(CLOSE_RE);
    if (open) {
      current = open[1];
    } else if (close && close[1] === current) {
      // 閉じ行自体はブロック内とはみなさない
      if (i < lineIndex) current = null;
    }
  }
  return current;
};
